import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import axios, { AxiosResponse, CancelToken } from 'axios';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { setInvalidError, setMissingError } from '~/actions/errorActions';
import {
  IAvailableMarkets,
  IBaseInput,
  IOrgNumberSettings,
} from '~/sharedInterfaces/SharedInterfaces';
import { normalizeIdentifier, getUrls } from '~/utils';
import { IBaseComponent } from '~/sharedInterfaces/IBaseComponent';
import { ORG_NUMBER } from '~/actions/actionTypes';
import { setInput, validateValue } from '~/actions/formActions';
import { IState } from '~/sharedInterfaces/IState';
import Input from '../Base/Input/Input';
import { getDefaultLabelName } from '~/services/DefaultLabelNameService';
import { setIsCompanyValid } from '~/actions/widgetContextActions';
import ComponentError from '../ComponentError/ComponentError';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import {
  getErrorMessage,
  IErrorMessages,
  IErrorWithValidation,
} from '~/services/ErrorMessageService';
import useDebounce from '~/hooks/useDebounce';
import { isValidCompanyNumber } from '~/utils/validators';
import { getCompanyNumberPlaceholder } from '~/services/GetPlaceholderService';

export interface IOrgNumber extends IBaseInput {}
export interface IOrgNumberProps extends IBaseComponent {}

const OrgNumber: FC<IOrgNumberProps> = (props) => {
  const dispatch = useDispatch();
  const value = useSelector((state: IState) => state.signup.orgNumber || '');
  const error = useSelector((state: IState) => state.error.orgNumber);
  const { locale } = useSelector((state: IState) => state.widgetContext);
  const componentLabels = getDefaultLabelName(locale, props.componentName) as IOrgNumberSettings;

  const [companyName, setCompanyName] = useState('');
  const [companyLookupRequestPending, setCompanyLookupRequestPending] = useState(false);
  const [companyInvalidError, setCompanyInvalidError] = useState('');
  const { invalid } = getErrorMessage(
    locale,
    ORG_NUMBER as keyof IErrorMessages
  ) as IErrorWithValidation;

  const debouncedOrgNumber = useDebounce(value);

  const lookUpOrgNumber = (
    orgNumber: string,
    market: keyof IAvailableMarkets<any>,
    cancelToken: CancelToken
  ) => {
    const { COMPANY_LOOKUP_URL } = getUrls();
    axios
      .get(`${COMPANY_LOOKUP_URL}/${normalizeIdentifier(orgNumber)}/${market}`, {
        cancelToken,
      })
      .then((res: AxiosResponse) => {
        setCompanyLookupRequestPending(false);
        dispatch(setIsCompanyValid(true));
        setCompanyName(res.data.companyInfo.name);
        setCompanyInvalidError('');
      })
      .catch((err) => {
        setCompanyLookupRequestPending(false);
        if (err.response && err.response.status === 404) {
          if (axios.isCancel(err)) return;
          dispatch(setIsCompanyValid(false));
          setCompanyName('');
          setCompanyInvalidError(invalid);
        } else {
          setCompanyName('');
          setCompanyInvalidError(componentLabels.lookupApiError);
          Sentry.captureException(new Error('Company lookup failed'), {
            tags: {
              category: 'Company lookup',
              orgNumber,
            },
          });
        }
      });
  };

  const handleValidation = () => {
    dispatch(validateValue(isValidCompanyNumber, ORG_NUMBER, value, locale));
  };

  useEffect(() => {
    const validOrgNumber = isValidCompanyNumber(value, props.settings.market);
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();
    if (validOrgNumber) {
      if (error?.invalid) dispatch(setInvalidError(ORG_NUMBER, ''));
      setCompanyLookupRequestPending(true);
      lookUpOrgNumber(value, props.settings.market, source.token);
    } else {
      setCompanyName('');
      setCompanyInvalidError('');
    }
    return () => {
      source.cancel();
    };
  }, [debouncedOrgNumber]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    dispatch(setInput(ORG_NUMBER, inputValue));
    if (error && error.missing) dispatch(setMissingError(ORG_NUMBER, ''));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const envDev =
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'test' ||
    process.env.NODE_ENV === 'verify';

  const renderOrgNumberLookup = () =>
    companyName && (
      <p className="qred-first">
        <span className="qred-first">
          <svg
            width="14"
            height="14"
            viewBox="0 0 200 200"
            fill="#fff"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M182.312 187.5H174.5V9.375C174.5 4.19727 170.303 0 165.125 0H33.875C28.6973 0 24.5 4.19727 24.5 9.375V187.5H16.6875C14.0988 187.5 12 189.599 12 192.188V200H187V192.188C187 189.599 184.901 187.5 182.312 187.5ZM62 29.6875C62 27.0988 64.0988 25 66.6875 25H82.3125C84.9012 25 87 27.0988 87 29.6875V45.3125C87 47.9012 84.9012 50 82.3125 50H66.6875C64.0988 50 62 47.9012 62 45.3125V29.6875ZM62 67.1875C62 64.5988 64.0988 62.5 66.6875 62.5H82.3125C84.9012 62.5 87 64.5988 87 67.1875V82.8125C87 85.4012 84.9012 87.5 82.3125 87.5H66.6875C64.0988 87.5 62 85.4012 62 82.8125V67.1875ZM82.3125 125H66.6875C64.0988 125 62 122.901 62 120.312V104.688C62 102.099 64.0988 100 66.6875 100H82.3125C84.9012 100 87 102.099 87 104.688V120.312C87 122.901 84.9012 125 82.3125 125ZM112 187.5H87V154.688C87 152.099 89.0988 150 91.6875 150H107.312C109.901 150 112 152.099 112 154.688V187.5ZM137 120.312C137 122.901 134.901 125 132.312 125H116.688C114.099 125 112 122.901 112 120.312V104.688C112 102.099 114.099 100 116.688 100H132.312C134.901 100 137 102.099 137 104.688V120.312ZM137 82.8125C137 85.4012 134.901 87.5 132.312 87.5H116.688C114.099 87.5 112 85.4012 112 82.8125V67.1875C112 64.5988 114.099 62.5 116.688 62.5H132.312C134.901 62.5 137 64.5988 137 67.1875V82.8125ZM137 45.3125C137 47.9012 134.901 50 132.312 50H116.688C114.099 50 112 47.9012 112 45.3125V29.6875C112 27.0988 114.099 25 116.688 25H132.312C134.901 25 137 27.0988 137 29.6875V45.3125Z"
              fill="#fff"
            />
          </svg>
          {companyName}
        </span>
      </p>
    );

  return (
    <div className="qred-orgnumber-wrapper">
      {envDev && companyInvalidError && (
        <button
          className="qred-development-validcompany"
          type="button"
          onClick={() => {
            dispatch(setIsCompanyValid(true));
            setCompanyName('Fake company');
            setCompanyInvalidError('');
          }}
        >
          Set company is valid
        </button>
      )}
      {companyLookupRequestPending && <LoadingSpinner />}

      <Input
        {...props}
        value={value}
        error={error ? error.missing || error.invalid : companyInvalidError}
        handleValidation={handleValidation}
        onChange={handleOnChange}
        analyticsEvent="org-nr"
        placeholder={getCompanyNumberPlaceholder(locale)}
      >
        {renderOrgNumberLookup()}
      </Input>
      {companyInvalidError && <ComponentError error={companyInvalidError} />}
    </div>
  );
};

export default OrgNumber;
